import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, of } from 'rxjs';
import { ICON } from '@shared/constants/icons.constant';
import { hasUnsupportedScope, SearchConnector, SearchResultItem, SearchScope } from '@shared/global-search';
import { Icon } from '@shared/types/icon.type';
import { Unit } from '../../../shared/models/entity/unit/unit.model';
import { UnitFilter } from '../../../shared/models/entity/unit/unit-filter.model';
import { UnitsService } from '../../../shared/services/entity/units.service';

const MAX_RESULTS = 6;

/** Searches the Einheiten by name/number — the Objekt shows as the subtitle. */
@Injectable({ providedIn: 'root' })
export class UnitsSearchConnector implements SearchConnector {
    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly entityService = inject(UnitsService);

    private readonly router = inject(Router);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    readonly id = 'units';

    readonly label = 'Einheiten';

    readonly order = 21;

    readonly icon: Icon = ICON.houseSimple;

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC METHODS
    // -----------------------------------------------------------------------------------------------------

    search(query: string, scope: SearchScope): Observable<SearchResultItem[]> {
        // Units honor the Objekt scope (via Building.PropertyId); other scopes skip them
        if (hasUnsupportedScope(scope, ['propertyId'])) {
            return of([]);
        }

        const propertyId = scope.context?.['propertyId'];

        return this.entityService
            .getAll({
                filter: {
                    filter: query,
                    propertyIds: propertyId ? [String(propertyId)] : [],
                } as UnitFilter,
                pagination: { page: 1, pageSize: MAX_RESULTS },
            })
            .pipe(map((items) => items.map((item) => this.toResultItem(item))));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE METHODS
    // -----------------------------------------------------------------------------------------------------

    private toResultItem(item: Unit): SearchResultItem {
        return {
            id: item.id,
            title: item.displayName || item.name || item.number,
            subtitle: item.propertyName,
            icon: this.icon,
            payload: item,
            actions: [
                {
                    id: 'open',
                    label: 'Öffnen',
                    icon: ICON.caretRight,
                    execute: () => {
                        this.router.navigate(['/units', item.id]);
                    },
                },
                {
                    id: 'scope',
                    label: 'Als Suchfilter verwenden',
                    icon: ICON.funnel,
                    keepOpen: true,
                    execute: (_, context) => {
                        context.setScope('unitId', item.id,
                            `Einheit: ${item.displayName || item.name || item.number}`);
                    },
                },
            ],
        };
    }
}
